import { z } from 'zod';
import { Progress } from './progress';
import { GOV_POLICY_IDS } from './gov';

// The day-one game loop: travel between places, do things there, keep your needs up. The server owns every
// number here (prices, wages, effects, time); the app only shows them and asks. Prices are real London prices in
// pence; wages follow the UK National Living Wage. Phase 4 moves this catalogue into the admin dashboard.

export const NEEDS = {
  hunger: { label: 'Hunger', icon: '🍔', perHour: 6 },
  energy: { label: 'Energy', icon: '⚡', perHour: 4 },
  hygiene: { label: 'Hygiene', icon: '🚿', perHour: 3 },
  fun: { label: 'Fun', icon: '🎉', perHour: 5 },
  social: { label: 'Social', icon: '💬', perHour: 4 },
  // full = comfortable; drinks fill it faster; a loo at home or in a venue empties it (Jerry, 9 October)
  bladder: { label: 'Bladder', icon: '🚽', perHour: 7 },
} as const;
export type NeedKey = keyof typeof NEEDS;
export const NEED_KEYS = Object.keys(NEEDS) as NeedKey[];
// what something does to the needs: a change per need (only the needs it touches)
export const NeedEffects = z.partialRecord(
  z.enum(NEED_KEYS as [NeedKey, ...NeedKey[]]),
  z.number().int().min(-100).max(100),
);
export type Needs = Record<NeedKey, number>;
export const START_NEEDS: Needs = { hunger: 80, energy: 90, hygiene: 85, fun: 70, social: 60, bladder: 90 };
export const NeedsSchema = z.object(
  Object.fromEntries(NEED_KEYS.map((key) => [key, z.number().min(0).max(100)])) as Record<NeedKey, z.ZodNumber>,
);
// reading a saved state: a need added later (bladder) is missing from older rows and reads as full
export const StoredNeedsSchema = z.object(
  Object.fromEntries(NEED_KEYS.map((key) => [key, z.number().min(0).max(100).default(100)])) as Record<
    NeedKey,
    z.ZodDefault<z.ZodNumber>
  >,
);

// the ways to travel and what they are like when the game starts; the admin changes fares, times and names in
// the content (world-content.ts `travel`), and the rules read the published ones. Seconds are real time on the
// road (the journey is shown on the map).
export const TRAVEL_MODES = {
  walk: { label: 'Walk', icon: '🚶', farePence: 0, seconds: 15, effects: { energy: -4, fun: 2 } },
  bus: { label: 'Bus', icon: '🚌', farePence: 175, seconds: 14, effects: {} },
  dlr: { label: 'DLR', icon: '🚈', farePence: 290, seconds: 10, effects: {} },
  tube: { label: 'Tube', icon: '🚇', farePence: 290, seconds: 10, effects: { hygiene: -2 } },
  cab: { label: 'Cab', icon: '🚕', farePence: 1600, seconds: 8, effects: {} },
} as const;
// the ways everyone can travel, and one that needs a car of your own (cars.ts): "Car" is on the travel sheet
// when the player keeps a car; its fuel and time come from the cars content, not the travel content
export type PublicTravelMode = keyof typeof TRAVEL_MODES;
export const CAR_MODE = 'car';
export type TravelMode = PublicTravelMode | typeof CAR_MODE;
export const TRAVEL_MODE_KEYS: [TravelMode, ...TravelMode[]] = [
  ...(Object.keys(TRAVEL_MODES) as PublicTravelMode[]),
  CAR_MODE,
] as unknown as [TravelMode, ...TravelMode[]];

// an activity is content the admin edits (world-content.ts); the rules read it from the published catalogue
export type { ActivityContent as Activity } from './world-content';
export const HOME_PLACE = 'home';
// most a life can earn from wages in one day (UK time), so no one can farm money by repeating a shift
export const DAILY_EARNING_CAP_PENCE = 30_000;

// Traits: up to three per life, chosen by the player and changeable once a week. Each one only changes how fast
// a need falls (decay) or how much an activity fills it (gain); none of them touches money.
type Multipliers = Partial<Record<NeedKey, number>>;
export const TRAITS = {
  social_butterfly: {
    label: 'Social butterfly',
    icon: '🦋',
    description: 'Needs people more, and gets more from them.',
    decay: { social: 1.25 },
    gain: { social: 1.25 },
  },
  homebody: {
    label: 'Homebody',
    icon: '🏠',
    description: 'Happy with their own company.',
    decay: { social: 0.8 },
    gain: {},
  },
  foodie: {
    label: 'Foodie',
    icon: '🍜',
    description: 'Hungry sooner, and loves a good meal.',
    decay: { hunger: 1.1 },
    gain: { hunger: 1.2 },
  },
  gym_rat: {
    label: 'Gym rat',
    icon: '💪',
    description: 'Fit: tires more slowly.',
    decay: { energy: 0.85 },
    gain: {},
  },
  neat_freak: {
    label: 'Neat freak',
    icon: '🧼',
    description: 'Stays fresh for longer.',
    decay: { hygiene: 0.8 },
    gain: { hygiene: 1.1 },
  },
  party_animal: {
    label: 'Party animal',
    icon: '🪩',
    description: 'Bored quickly, but a night out goes a long way.',
    decay: { fun: 1.2 },
    gain: { fun: 1.3 },
  },
  night_owl: {
    label: 'Night owl',
    icon: '🦉',
    description: 'Runs on less sleep, and a nap does wonders.',
    decay: { energy: 0.9 },
    gain: { energy: 1.15 },
  },
  iron_bladder: {
    label: 'Iron bladder',
    icon: '🫖',
    description: 'Can hold on through a long quiz.',
    decay: { bladder: 0.7 },
    gain: {},
  },
} as const satisfies Record<
  string,
  { label: string; icon: string; description: string; decay: Multipliers; gain: Multipliers }
>;
export type TraitKey = keyof typeof TRAITS;
export const TRAIT_KEYS = Object.keys(TRAITS) as TraitKey[];
export const MAX_TRAITS = 3;
export const TRAIT_CHANGE_DAYS = 7;
export const TraitsUpdate = z
  .object({
    traits: z
      .array(z.enum(TRAIT_KEYS as [TraitKey, ...TraitKey[]]))
      .max(MAX_TRAITS)
      .refine((list) => new Set(list).size === list.length, 'Pick each trait once'),
  })
  .strict();
export type TraitsUpdate = z.infer<typeof TraitsUpdate>;
const multiplier = (traits: readonly TraitKey[], kind: 'decay' | 'gain', key: NeedKey) =>
  traits.reduce((product, trait) => product * ((TRAITS[trait][kind] as Multipliers)[key] ?? 1), 1);

export const PlayState = z.object({
  placeId: z.string(),
  needs: NeedsSchema,
  busyUntil: z.string().nullable(),
  busyLabel: z.string().nullable(),
  // what the character is doing now (an activity, or something with someone): since when, and whether it can
  // be stopped (Lagos Life's "tap to cancel"); null on the way somewhere or when free
  doing: z.object({ since: z.string(), cancellable: z.boolean() }).nullish(),
  walletPence: z.number().int(),
  earnedTodayPence: z.number().int(),
  earningCapPence: z.number().int(),
  // the life's traits (they change how needs move) and the facts feelings are worked out from
  traits: z.array(z.enum(TRAIT_KEYS as [TraitKey, ...TraitKey[]])),
  lastPaidAt: z.string().nullable(),
  lifeStartedAt: z.string().nullable(),
  // skills and careers (progress.ts)
  progress: Progress,
  // ill (health.ts): what with and how badly, for the feelings and the status bar
  sick: z.object({ name: z.string(), emoji: z.string(), level: z.enum(['mild', 'serious']) }).nullish(),
  // the car being driven (cars.ts), for "Car" on the travel sheet: its name, what a trip costs in fuel (nothing
  // with a driver on the payroll), how long it takes, and whether it is too worn to start
  car: z
    .object({
      model: z.string(),
      label: z.string(),
      nick: z.string(),
      emoji: z.string(),
      colour: z.string(),
      bike: z.boolean(),
      fuelPence: z.number().int(),
      seconds: z.number().int(),
      driver: z.boolean(),
      condition: z.number(),
      tired: z.boolean(),
    })
    .nullish(),
  // the Mayor's policy in force (gov.ts): prices and pay on the screens follow it as the server's do
  policy: z.enum(GOV_POLICY_IDS).nullish(),
});
export type PlayState = z.infer<typeof PlayState>;
// kept with the life's state while it does something: when it started and ends, and what it gained, so stopping
// it early takes back the part not done (the needs and skill points); a shift or a class cannot be stopped
export const BusyGains = z.object({
  since: z.string(),
  until: z.string(),
  cancellable: z.boolean(),
  needs: z.record(z.string(), z.number()),
  skills: z.record(z.string(), z.number()),
});
export type BusyGains = z.infer<typeof BusyGains>;
export const PlayReply = z.object({ state: PlayState, message: z.string().optional() });
export type PlayReply = z.infer<typeof PlayReply>;
export const TravelRequest = z
  .object({
    placeId: z.string().regex(/^[a-z0-9_]{2,40}$/),
    mode: z.enum(TRAVEL_MODE_KEYS),
  })
  .strict();
export const ActivityRequest = z
  .object({
    activityId: z.string().regex(/^[a-z0-9_]{2,40}$/),
    // which one, where an activity has choices (a class: the course)
    choice: z
      .string()
      .regex(/^[a-z0-9_]{2,40}$/)
      .optional(),
  })
  .strict();

// a mood from the needs, for the status bar
export function moodOf(needs: Needs): { label: string; icon: string } {
  const average = NEED_KEYS.reduce((sum, key) => sum + needs[key], 0) / NEED_KEYS.length;
  const lowest = Math.min(...NEED_KEYS.map((key) => needs[key]));
  if (lowest < 10) return { label: 'Struggling', icon: '😣' };
  if (average >= 75) return { label: 'Thriving', icon: '😄' };
  if (average >= 55) return { label: 'Settled', icon: '🙂' };
  if (average >= 35) return { label: 'Tired', icon: '😕' };
  return { label: 'Miserable', icon: '😞' };
}
// needs fall with real time; this is the one formula, used by the server (and by the app between replies)
export function needsAt(needs: Needs, since: Date, now: Date, traits: readonly TraitKey[] = []): Needs {
  const hours = Math.max(0, now.getTime() - since.getTime()) / 3_600_000;
  return Object.fromEntries(
    NEED_KEYS.map((key) => {
      const rate = NEEDS[key].perHour * multiplier(traits, 'decay', key);
      return [key, Math.max(0, Math.round((needs[key] - rate * hours) * 10) / 10)];
    }),
  ) as Needs;
}
// an activity's effects; traits only boost what fills a need, never what drains it
export function applyEffects(
  needs: Needs,
  effects: Partial<Record<NeedKey, number>>,
  traits: readonly TraitKey[] = [],
): Needs {
  return Object.fromEntries(
    NEED_KEYS.map((key) => {
      const change = effects[key] ?? 0;
      const boosted = change > 0 ? change * multiplier(traits, 'gain', key) : change;
      return [key, Math.min(100, Math.max(0, Math.round((needs[key] + boosted) * 10) / 10))];
    }),
  ) as Needs;
}

// Feelings: what the character feels right now and why, each worth mood points (good or bad). Worked out from the
// needs and a few facts the server sends, so the app and the server always agree.
export type Feeling = { key: string; label: string; icon: string; points: number; reason: string };
export function feelingsOf(
  needs: Needs,
  facts: {
    lastPaidAt?: string | null;
    lifeStartedAt?: string | null;
    now?: Date;
    sick?: { name: string; emoji: string; level: 'mild' | 'serious' } | null;
  } = {},
): Feeling[] {
  const now = facts.now ?? new Date();
  const feelings: Feeling[] = [];
  const add = (key: string, label: string, icon: string, points: number, reason: string) =>
    feelings.push({ key, label, icon, points, reason });
  if (facts.sick?.level === 'serious')
    add('very_ill', 'Very ill', facts.sick.emoji, -25, `${facts.sick.name}, and it's serious: see a doctor`);
  else if (facts.sick)
    add('unwell', 'Unwell', facts.sick.emoji, -10, `Coming down with ${facts.sick.name.toLowerCase()}`);
  if (needs.hunger < 15) add('starving', 'Starving', '😫', -20, 'Has not eaten for far too long');
  else if (needs.hunger < 35) add('hungry', 'Hungry', '🍽️', -8, 'Could do with something to eat');
  else if (needs.hunger > 85) add('well_fed', 'Well fed', '😋', 6, 'Had a good meal');
  if (needs.energy < 15) add('exhausted', 'Exhausted', '🥱', -18, 'Needs to sleep');
  else if (needs.energy < 35) add('tired', 'Tired', '😴', -6, 'Running low on energy');
  else if (needs.energy > 85) add('energised', 'Energised', '⚡', 6, 'Well rested');
  if (needs.hygiene < 20) add('grubby', 'Grubby', '🦨', -12, 'Needs a shower; people notice');
  else if (needs.hygiene > 85) add('fresh', 'Fresh', '🧴', 5, 'Just showered');
  if (needs.fun < 20) add('bored', 'Bored', '😐', -10, 'Nothing fun for a while');
  else if (needs.fun > 85) add('having_fun', 'Having fun', '🎉', 8, 'Enjoying life in London');
  if (needs.social < 20) add('lonely', 'Lonely', '😔', -12, 'Has not seen anyone for a while');
  else if (needs.social > 85) add('connected', 'Connected', '🤝', 8, 'Spending time with people');
  if (needs.bladder < 10) add('bursting', 'Bursting', '😖', -25, 'Find a loo now');
  else if (needs.bladder < 30) add('need_the_loo', 'Needs the loo', '🚻', -6, 'Should find a loo soon');
  if (facts.lastPaidAt && now.getTime() - Date.parse(facts.lastPaidAt) < 3 * 3_600_000)
    add('payday', 'Payday', '💷', 10, 'Just got paid');
  if (facts.lifeStartedAt && now.getTime() - Date.parse(facts.lifeStartedAt) < 3 * 86_400_000)
    add('new_in_town', 'New in London', '🗺️', 5, 'Everything is new and exciting');
  return feelings;
}
export const moodPoints = (feelings: Feeling[]) => feelings.reduce((sum, feeling) => sum + feeling.points, 0);
